import {isFeatureEnabled} from 'feature-flags';
import {Chip, Choice, Segment, SegmentProps} from 'platform/components';
import {Box, HStack, Text} from 'platform/foundation';

import {useMemo} from 'react';

import {getSafely, suffixTestId, TestIdProps} from 'shared/web';

import i18n from '../../../../../i18n/i18n';
import {featureFlags} from '../../../../../utils/featureFlags';
import {CHART_TOOLBAR_ALL_DEALERS} from '../consts/chartToolbarAllDealers';
import {useChartToolbar} from '../hooks/useChartToolbar';
import {ChartToolbarHighlight} from '../types/ChartToolbarHighlight';
import {ChartToolbarHighlightMode} from '../types/ChartToolbarHighlightMode';
import {ExtendedStatisticsCar} from '../types/ExtendedStatisticsCar';
import {getStatisticsChartSellerOptions} from '../utils/getStatisticsChartSellerOptions';
import {hasActiveChartToolbarFilters} from '../utils/hasActiveChartToolbarHighlights';

interface ChartToolbarProps extends TestIdProps {
  data: ExtendedStatisticsCar[];
}

export function ChartToolbar(props: ChartToolbarProps) {
  const {
    highlights,
    effectiveHighlightMode,
    selectedSellerId,
    setHighlightMode,
    setSelectedSellerId,
    toggleHighlight,
  } = useChartToolbar();
  const isSuspiciousFlagEnabled = isFeatureEnabled(featureFlags.PR_SUSPICIOUS_FLAG);
  const isSellerTypeEnabled = isFeatureEnabled(featureFlags.PR_FILTERS_SELLER_TYPE);
  const isHighlightModeActive = hasActiveChartToolbarFilters(highlights, selectedSellerId);

  const sellerOptions = useMemo(() => {
    const uniqueSellers = getStatisticsChartSellerOptions(props.data);

    return [
      {
        value: CHART_TOOLBAR_ALL_DEALERS,
        label: i18n.t('entity.statisticsChart.toolbar.allDealers'),
      },
      ...uniqueSellers,
    ];
  }, [props.data]);

  const renderChip = (highlight: ChartToolbarHighlight, label: string) => (
    <Chip
      label={label}
      value={getSafely(highlights, highlight, false)}
      onChange={() => toggleHighlight(highlight)}
      size="small"
      data-testid={suffixTestId(`${highlight}Chip`, props)}
    />
  );

  const highlightModeOptions: NonNullable<SegmentProps<ChartToolbarHighlightMode>['options']> = [
    {value: 'all', label: i18n.t('entity.statisticsChart.toolbar.mode.all')},
    {
      value: 'dimOthers',
      label: i18n.t('entity.statisticsChart.toolbar.mode.dimOthers'),
      isDisabled: !isHighlightModeActive,
    },
    {
      value: 'hideOthers',
      label: i18n.t('entity.statisticsChart.toolbar.mode.hideOthers'),
      isDisabled: !isHighlightModeActive,
    },
  ];

  return (
    <Box
      backgroundColor="palettes.neutral.10.100"
      border="1px solid"
      borderColor="palettes.neutral.30.100"
      borderRadius="small"
      paddingHorizontal={2}
      paddingVertical={1}
      data-testid={suffixTestId('wrapper', props)}
    >
      <HStack spacing={4} wrap align="center">
        <HStack spacing={2} wrap align="center">
          <Text size="xSmall" color="secondary" alternative>
            {`${i18n.t('entity.statisticsChart.toolbar.highlight')}:`}
          </Text>
          {isSellerTypeEnabled &&
            renderChip('sellerType', i18n.t('entity.statisticsChart.toolbar.privateSeller'))}
          {isSuspiciousFlagEnabled &&
            renderChip('suspicious', i18n.t('page.advertisiments.suspicious'))}
          {renderChip('new', i18n.t('entity.statisticsChart.toolbar.new'))}
          {renderChip('repriced', i18n.t('entity.statisticsChart.toolbar.repriced'))}
        </HStack>
        <HStack spacing={2} align="center">
          <Text size="xSmall" color="secondary" alternative>
            {`${i18n.t('entity.statisticsChart.toolbar.dealer')}:`}
          </Text>
          <Box width={48}>
            <Choice
              value={selectedSellerId ?? CHART_TOOLBAR_ALL_DEALERS}
              options={sellerOptions}
              onChange={(value) =>
                setSelectedSellerId(
                  value === CHART_TOOLBAR_ALL_DEALERS || value == null ? null : value
                )
              }
              isNotClearable
              horizontalMenuPlacement="right"
              data-testid={suffixTestId('sellerChoice', props)}
            />
          </Box>
        </HStack>
        <HStack spacing={2} wrap align="center">
          <Text size="xSmall" color="secondary" alternative>
            {`${i18n.t('entity.statisticsChart.toolbar.modeLabel')}:`}
          </Text>
          <Segment
            value={effectiveHighlightMode}
            options={highlightModeOptions}
            onChange={setHighlightMode}
            data-testid={suffixTestId('highlightMode', props)}
          />
        </HStack>
      </HStack>
    </Box>
  );
}
